import React from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonProps,
  Checkbox,
  FormControlLabel,
  Slider,
  Typography,
} from '@mui/material';

import { MoveBrowserClient } from 'move-sdk';
import ExpandableCard from '../../../../../Components/ExpandableCard';
import {
  EDGE_FILTER_CLASSES,
  EDGE_FILTER_KEYS,
  EDGE_FILTER_TYPES,
  validateEdgeFilterUpdate,
} from '../../../../../modules/move/edgeFilter';

// Sends detectionTypes and flat confidence percentages through updateConfig.
// Motion is selectable but has no confidence setting. The panel reflects the
// last reported config separately from the values being edited.

type EdgeType = (typeof EDGE_FILTER_TYPES)[number];
type EdgeClass = (typeof EDGE_FILTER_CLASSES)[number];

const buttonProps: ButtonProps = {
  size: 'small',
  color: 'secondary',
  variant: 'outlined',
  fullWidth: true,
};

const DEFAULT_CONFIDENCES: Record<EdgeClass, number> = {
  person: 75,
  vehicle: 80,
  animal: 70,
  package: 85,
};

function readReported(config: CameraConfig | undefined) {
  const raw = (config || {}) as Record<string, unknown>;
  const types = Array.isArray(raw.detectionTypes)
    ? (raw.detectionTypes as unknown[]).filter(
        (t): t is EdgeType =>
          typeof t === 'string' && (EDGE_FILTER_TYPES as string[]).includes(t),
      )
    : [];
  const confidences: Partial<Record<EdgeClass, number>> = {};
  EDGE_FILTER_CLASSES.forEach((type) => {
    const value = raw[`${type}Confidence`];
    if (typeof value === 'number') {
      confidences[type] = value;
    }
  });
  return {
    present: EDGE_FILTER_KEYS.some((key) => key in raw),
    types,
    confidences,
  };
}

export default function EdgeFilterControl(props: {
  device: MoveDevice;
  config: CameraConfig | undefined;
  moveClient: MoveBrowserClient;
  channel: string;
}) {
  const deviceId = props.device.addresses[0].address;
  const reported = React.useMemo(
    () => readReported(props.config),
    [props.config],
  );

  const [types, setTypes] = React.useState<EdgeType[]>(reported.types);
  const [confidences, setConfidences] = React.useState<
    Record<EdgeClass, number>
  >(
    () =>
      Object.fromEntries(
        EDGE_FILTER_CLASSES.map((t) => [
          t,
          reported.confidences[t] ?? DEFAULT_CONFIDENCES[t],
        ]),
      ) as Record<EdgeClass, number>,
  );
  const [error, setError] = React.useState<string | null>(null);
  const [lastSent, setLastSent] = React.useState<string | null>(null);

  // Re-sync the form when the camera reports a new config.
  React.useEffect(() => {
    setTypes(reported.types);
    setConfidences(
      Object.fromEntries(
        EDGE_FILTER_CLASSES.map((t) => [
          t,
          reported.confidences[t] ?? DEFAULT_CONFIDENCES[t],
        ]),
      ) as Record<EdgeClass, number>,
    );
    setError(null);
    setLastSent(null);
  }, [reported, props.channel, deviceId]);

  const send = (data: Record<string, unknown>) => {
    setError(null);
    try {
      validateEdgeFilterUpdate(data);
    } catch (e) {
      setError((e as Error).message);
      return;
    }
    let payload: Record<string, unknown> = data;
    if (props.channel) {
      payload = { [`channel-${props.channel}`]: { ...data } };
    }
    props.moveClient.sendToCamera(
      deviceId,
      'updateConfig',
      payload,
    );
    setLastSent(JSON.stringify(data));
  };

  const applyAll = () => {
    send({
      detectionTypes: types,
      ...Object.fromEntries(
        EDGE_FILTER_CLASSES.map((type) => [
          `${type}Confidence`,
          confidences[type],
        ]),
      ),
    });
  };

  const toggleType = (type: EdgeType) => {
    setTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  return (
    <ExpandableCard title="Edge Detection Settings" smallMargin>
      <Box display="flex" flexDirection="column" rowGap={1}>
        <Typography variant="body2" color="text.secondary">
          Reported by camera:{' '}
          {!reported.present
            ? 'none — no detection settings in getConfig'
            : `detectionTypes=[${reported.types.join(
                ', ',
              )}] confidences=${JSON.stringify(reported.confidences)}`}
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={types.includes('motion')}
              onChange={() => toggleType('motion')}
            />
          }
          label="Generic motion (unidentified motion)"
        />

        {EDGE_FILTER_CLASSES.map((type) => {
          const selected = types.includes(type);
          return (
            <Box key={type} display="flex" flexDirection="column">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={selected}
                    onChange={() => toggleType(type)}
                  />
                }
                label={`${type} — ${confidences[type]}% confidence`}
              />
              <Slider
                size="small"
                aria-label={`${type} confidence`}
                min={0}
                max={100}
                step={1}
                value={confidences[type]}
                onChange={(_, value) =>
                  setConfidences((prev) => ({
                    ...prev,
                    [type]: value as number,
                  }))
                }
                sx={{ mx: 1 }}
              />
            </Box>
          );
        })}

        {error && <Alert severity="error">{error}</Alert>}

        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-evenly"
          columnGap={1}
        >
          <Button {...buttonProps} variant="contained" onClick={applyAll}>
            Apply
          </Button>
        </Box>

        {lastSent && (
          <Typography variant="caption" color="text.secondary">
            Last sent: {lastSent}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          Apply sends the selected detection types and all four confidence
          settings. Generic Motion has no confidence setting.
        </Typography>
      </Box>
    </ExpandableCard>
  );
}
